import React from "react";
import { motion } from "framer-motion";
import { ListChecks, Plus, Download, Trash2, Users, FolderOpen } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LeadsTable } from "@/components/LeadsTable";
import { LeadDetailDrawer } from "@/components/LeadDetailDrawer";
import { useT } from "@/i18n/LanguageContext";

const STATUS_KEYS = ["All", "New", "Contacted", "Replied", "Interested", "Won", "Lost"];

export default function Lists() {
  const t = useT();
  const [lists, setLists] = React.useState([]);
  const [activeId, setActiveId] = React.useState(null);
  const [listData, setListData] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState("All");
  const [selected, setSelected] = React.useState([]);
  const [newName, setNewName] = React.useState("");
  const [newDesc, setNewDesc] = React.useState("");
  const [createOpen, setCreateOpen] = React.useState(false);
  const [options, setOptions] = React.useState({});
  const [detail, setDetail] = React.useState(null);
  const [detailOpen, setDetailOpen] = React.useState(false);

  const loadLists = async () => {
    const d = await api.getLists();
    setLists(d.lists || []);
    if (!activeId && d.lists && d.lists.length) setActiveId(d.lists[0].id);
    return d.lists || [];
  };

  React.useEffect(() => {
    loadLists();
    api.options().then(setOptions).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (activeId) {
      api.getList(activeId).then(setListData).catch(() => setListData(null));
      setSelected([]);
      setStatusFilter("All");
    } else setListData(null);
  }, [activeId]);

  const reloadActive = async () => { if (activeId) setListData(await api.getList(activeId)); loadLists(); };

  const createList = async () => {
    if (!newName.trim()) return;
    const created = await api.createList(newName.trim(), newDesc.trim());
    toast.success(t("list_created", { name: created.name }));
    setNewName(""); setNewDesc(""); setCreateOpen(false);
    await loadLists();
    setActiveId(created.id);
  };

  const deleteList = async (id) => {
    await api.deleteList(id);
    toast.success(t("list_deleted"));
    const ls = await loadLists();
    setActiveId(ls.length ? ls[0].id : null);
  };

  const removeSelected = async () => {
    await api.removeFromList(activeId, selected);
    toast.success(t("removed_n_leads", { n: selected.length }));
    setSelected([]);
    reloadActive();
  };

  const exportCsv = () => {
    if (!activeId) return;
    window.open(api.exportListUrl(activeId), "_blank");
    toast.success(t("exporting_csv"));
  };

  const leads = (listData?.leads || []).filter((l) => statusFilter === "All" || l.status === statusFilter);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected((s) => (s.length === leads.length ? [] : leads.map((l) => l.id)));

  const openDetail = (lead) => { setDetail(lead); setDetailOpen(true); };
  const onLeadChanged = (updated) => {
    setListData((d) => d ? { ...d, leads: d.leads.map((l) => (l.id === updated.id ? updated : l)) } : d);
    setDetail(updated);
  };

  const statusLabel = (k) => (k === "All" ? t("all") : k);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">{t("lists_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("lists_subtitle")}</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="lists-create-list-button"><Plus className="mr-1.5 h-4 w-4" /> {t("new_list")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("create_new_list")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("list_name")}</Label><Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t("list_name_placeholder")} data-testid="new-list-name-input" className="mt-1" /></div>
              <div><Label>{t("description_optional")}</Label><Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder={t("description_placeholder")} className="mt-1" /></div>
            </div>
            <DialogFooter><Button onClick={createList} data-testid="new-list-submit-button">{t("create_list")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {lists.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-3 p-12 text-center">
          <FolderOpen className="h-8 w-8 text-muted-foreground" />
          <div className="font-medium">{t("create_first_list")}</div>
          <p className="max-w-sm text-sm text-muted-foreground">{t("create_first_list_sub")}</p>
          <Button onClick={() => setCreateOpen(true)}><Plus className="mr-1.5 h-4 w-4" /> {t("new_list")}</Button>
        </Card>
      ) : (
        <div className="flex flex-col gap-6 lg:flex-row">
          <aside className="w-full shrink-0 lg:w-[260px]">
            <div className="space-y-1.5">
              {lists.map((l) => (
                <button key={l.id} data-testid="lists-list-item" onClick={() => setActiveId(l.id)}
                  className={`flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left transition-colors ${activeId === l.id ? "border-primary bg-primary/5" : "bg-card hover:bg-secondary/60"}`}>
                  <div className="flex items-center gap-2 overflow-hidden">
                    <ListChecks className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="overflow-hidden">
                      <div className="truncate text-sm font-medium">{l.name}</div>
                      {l.description && <div className="truncate text-xs text-muted-foreground">{l.description}</div>}
                    </div>
                  </div>
                  <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-xs tabular-nums">{l.count}</span>
                </button>
              ))}
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            {listData && (
              <>
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="font-heading text-lg font-semibold">{listData.name}</span>
                    <span className="text-sm text-muted-foreground">({listData.count} {t("leads_count")})</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={exportCsv} data-testid="lists-export-csv-button"><Download className="mr-1.5 h-4 w-4" /> {t("export_csv")}</Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" data-testid="lists-delete-button"><Trash2 className="mr-1.5 h-4 w-4" /> {t("delete")}</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>{t("delete_list_q")}</AlertDialogTitle><AlertDialogDescription>{t("delete_list_desc", { name: listData.name })}</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>{t("cancel")}</AlertDialogCancel><AlertDialogAction onClick={() => deleteList(activeId)}>{t("delete")}</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mb-3">
                  <TabsList className="flex-wrap">
                    {STATUS_KEYS.map((k) => (
                      <TabsTrigger key={k} value={k} data-testid="lists-status-tab">{statusLabel(k)}</TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>

                {selected.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="mb-3 flex items-center gap-2 rounded-xl border bg-card p-2 shadow-sm">
                    <span className="px-2 text-sm font-medium">{t("selected", { n: selected.length })}</span>
                    <Button variant="outline" size="sm" onClick={removeSelected} data-testid="lists-remove-selected"><Trash2 className="mr-1.5 h-4 w-4" /> {t("remove_from_list")}</Button>
                  </motion.div>
                )}

                {leads.length === 0 ? (
                  <Card className="flex flex-col items-center justify-center gap-2 p-12 text-center">
                    <div className="font-medium">{statusFilter !== "All" ? t("no_leads_status", { status: statusFilter }) : t("no_leads_in_list")}</div>
                    <p className="text-sm text-muted-foreground">{t("add_leads_from_search")}</p>
                  </Card>
                ) : (
                  <LeadsTable leads={leads} selected={selected} onToggle={toggle} onToggleAll={toggleAll} onOpen={openDetail} />
                )}
              </>
            )}
          </div>
        </div>
      )}

      <LeadDetailDrawer lead={detail} open={detailOpen} onOpenChange={setDetailOpen} statusOptions={options.status} lists={lists} onChanged={onLeadChanged} onAddToList={async (id, ids) => { await api.addToList(id, ids); toast.success(t("added_to", { name: "" })); loadLists(); }} />
    </div>
  );
}
